import { nestedNodeDelimiter, } from "@buildwithflux/models";
import { guid } from "@buildwithflux/shared";
export function getOrderedNetPins(nets, netId) {
    const net = nets[netId];
    if (!net) {
        throw new Error(`Net ${netId} does not exist`);
    }
    return Object.values(net.pins).sort((a, b) => {
        if (!a.order || !b.order) {
            return 0;
        }
        if (a.order < b.order) {
            return -1;
        }
        if (a.order > b.order) {
            return 1;
        }
        return 0;
    });
}
/**
 * Create a new net with the given ID and pins, and add it to `nets`.
 */
export function addNet(nets, hash, label, pinIds, properties = {}, 
// This flag is used to preserve the original behavior of migration where
// the net uid was set as the net hash. This will be maintained as the net UID
// during merges and splits.
migration = false) {
    const pins = createPinMapFromPinIds(pinIds);
    const net = {
        uid: migration ? hash : guid(),
        hash,
        label,
        pins,
        properties,
        nextPinOrder: pinIds.length,
    };
    nets[net.uid] = net;
    return net;
}
/**
 * Add the pins (in order) to the specified net. Update existing pins if already present.
 */
export function addPinsToNet(nets, netId, pinsIds) {
    const net = nets[netId];
    if (!net) {
        throw new Error(`Net ${netId} does not exist`);
    }
    for (const pinId of pinsIds) {
        const newPin = createPinFromPinId(pinId);
        const existingPin = net.pins[pinId];
        if (existingPin) {
            // Only update pins if it has changed. This avoids unnecessary patch changes.
            if (existingPin.elementUid !== newPin.elementUid || existingPin.terminalUid !== newPin.terminalUid) {
                net.pins[newPin.uid] = { ...existingPin, ...newPin };
            }
        }
        else {
            net.pins[newPin.uid] = { ...newPin, order: net.nextPinOrder };
            net.nextPinOrder++;
        }
    }
}
/**
 * Remove the specified pins from the net
 */
export function removePinsFromNet(nets, netId, pins) {
    const net = nets[netId];
    if (!net) {
        throw new Error(`Net ${netId} does not exist`);
    }
    for (const pin of pins) {
        delete net.pins[pin];
    }
}
/**
 * Get all pins in a net (the "new" net) that were present in another net (the "old" net) but are not anymore.
 */
export function getMissingPins(newNetMembers, oldNet) {
    const result = [];
    for (const pin of Object.values(oldNet.pins)) {
        const inNewNet = !!newNetMembers[pin.uid];
        if (!inNewNet) {
            result.push(pin.uid);
        }
    }
    return result;
}
/**
 * Get all pins in a net (the "new" net) that are not present in another net (the "old" net).
 */
export function getNewPins(newNetMembers, oldNet) {
    const result = [];
    for (const pin of Object.keys(newNetMembers)) {
        const inOldNet = !!oldNet.pins[pin];
        if (!inOldNet) {
            result.push(pin);
        }
    }
    return result;
}
/**
 * Clear out all pins on the net
 */
export function removeAllPinsFromNet(nets, netId) {
    const net = nets[netId];
    if (!net) {
        throw new Error(`Net ${netId} does not exist`);
    }
    net.pins = {};
    net.nextPinOrder = 0;
}
/**
 * Convert the collection of nets back into a map of net hashes to pins.
 */
export function getNetHashMap(nets) {
    return Object.fromEntries(Object.values(nets).map((net) => [
        // Until we have a migration in place, need to fallback to uid when hash is not set
        net.hash ?? net.uid,
        Object.fromEntries(Object.keys(net.pins).map((pinId) => [pinId, true])),
    ]));
}
function createPinMapFromPinIds(pinIds, nextPinOrder = 0) {
    return pinIds.reduce((acc, uid, index) => {
        const pin = createPinFromPinId(uid);
        pin.order = nextPinOrder + index;
        acc[uid] = pin;
        return acc;
    }, {});
}
function createPinFromPinId(pinId) {
    const [elementUid, terminalUid] = pinId.split(nestedNodeDelimiter);
    return { uid: pinId, elementUid: elementUid, terminalUid: terminalUid };
}
export function createNetUidToTerminalsMap(netNodes) {
    return netNodes.reduce((acc, node) => {
        acc[node.uid] = node.terminals || {};
        return acc;
    }, {});
}
//# sourceMappingURL=nets.js.map